import { Card, CardContent, Typography, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Product } from "../../../../interfaces/DTO/mislaka_xml_models";
import { formatCurrency } from "../../../../utils/namesFormater";
import { formatInsuranceType } from "../../../../utils/pensionEnumFormatter";
import { useMemo } from "react";

interface ProductInsuranceFeeSummaryProps {
  product: Product;
}

export const ProductInsuranceFeeSummary: React.FC<ProductInsuranceFeeSummaryProps> = ({ product }) => {
  const coverages = product.policies_or_accounts.flatMap(pa => pa.insurence_coverages || []);

  // זיהוי מבוטח ראשי ומבוטחים אחרים
  const insuredTypes = useMemo(() => {
    const clientId = product.client?.client_id;
    const otherInsured = new Map<string, string>();

    coverages.forEach((coverage) => {
      coverage.insured_details?.forEach((detail) => {
        if (detail.id_number && detail.id_number !== clientId) {
          otherInsured.set(detail.id_number, `מבוטח ${detail.id_number}`);
        }
      });
    });

    return {
      primaryInsuredId: clientId,
      otherInsured: Array.from(otherInsured.entries()).map(([id, name]) => ({ id, name }))
    };
  }, [coverages, product.client?.client_id]);

  // קיבוץ כיסויים לפי מבוטח
  const coveragesByInsured = useMemo(() => {
    const grouped = new Map<string | null, typeof coverages>();

    coverages.forEach((coverage) => {
      if (!coverage.coverage_details?.insurance_fees?.fee) return;

      // אם אין insured_details או יש את המבוטח הראשי
      if (!coverage.insured_details || coverage.insured_details.length === 0 ||
          coverage.insured_details.some(detail => detail.id_number === insuredTypes.primaryInsuredId)) {
        const primaryCoverages = grouped.get(null) || [];
        primaryCoverages.push(coverage);
        grouped.set(null, primaryCoverages);
      }

      // הוסף למבוטחים אחרים
      coverage.insured_details?.forEach(detail => {
        if (detail.id_number && detail.id_number !== insuredTypes.primaryInsuredId) {
          const otherCoverages = grouped.get(detail.id_number) || [];
          otherCoverages.push(coverage);
          grouped.set(detail.id_number, otherCoverages);
        }
      });
    });

    return grouped;
  }, [coverages, insuredTypes.primaryInsuredId]);

  const totalFee = coverages.reduce((sum, c) => sum + (c.coverage_details?.insurance_fees?.fee || 0), 0);

  // בדיקות אחרי כל ה-hooks
  if (coverages.length === 0) return null;
  if (totalFee === 0) return null;

  // אם יש רק מבוטח ראשי - תצוגה רגילה
  if (insuredTypes.otherInsured.length === 0) {
    const sortedCoverages = coverages
      .filter(c => c.coverage_details?.insurance_fees?.fee != null)
      .sort((a, b) => (b.coverage_details!.insurance_fees!.fee - a.coverage_details!.insurance_fees!.fee));

    return (
      <Card className="rounded-2xl shadow-md p-4">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            סיכום דמי ביטוח - {formatCurrency(totalFee)}
          </Typography>
          <Grid container spacing={2}>
            {sortedCoverages.map((coverage, idx) => {
              const name = coverage.coverage_name;
              const kind = formatInsuranceType(coverage.coverage_details!.insurance_kind);
              const fee = coverage.coverage_details!.insurance_fees!.fee;
              return (
                <Grid size={{ xs: 12 }} key={idx}>
                  <Typography variant="body1">
                    {name}: <strong>{formatCurrency(fee)}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kind}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  // אם יש מבוטחים נוספים - תצוגה בעמודות
  const renderInsuredColumn = (insuredId: string | null, title: string) => {
    const insuredCoverages = coveragesByInsured.get(insuredId) || [];
    const sortedCoverages = insuredCoverages
      .filter(c => c.coverage_details?.insurance_fees?.fee != null)
      .sort((a, b) => (b.coverage_details!.insurance_fees!.fee - a.coverage_details!.insurance_fees!.fee));
    const total = sortedCoverages.reduce((sum, c) => sum + (c.coverage_details?.insurance_fees?.fee || 0), 0);

    if (sortedCoverages.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title} - {formatCurrency(total)}
        </Typography>
        {sortedCoverages.map((coverage, idx) => {
          const name = coverage.coverage_name;
          const kind = formatInsuranceType(coverage.coverage_details!.insurance_kind);
          const fee = coverage.coverage_details!.insurance_fees!.fee;
          return (
            <Box key={idx} sx={{ mb: 1, pl: 2 }}>
              <Typography variant="body2">
                {name}: <strong>{formatCurrency(fee)}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {kind}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Card className="rounded-2xl shadow-md p-4">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          סיכום דמי ביטוח - {formatCurrency(totalFee)}
        </Typography>
        <Grid container spacing={3}>
          {/* עמודת מבוטח ראשי */}
          <Grid size={{ xs: 12, md: 6 }}>
            {renderInsuredColumn(null, "מבוטח ראשי")}
          </Grid>

          {/* עמודות מבוטחים אחרים */}
          {insuredTypes.otherInsured.map(({ id, name }) => (
            <Grid size={{ xs: 12, md: 6 }} key={id}>
              {renderInsuredColumn(id, name)}
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};
